/**
 * MultiSelectContextMenu — context menu for multiple selected tasks.
 *
 * Shown on right-click when ≥2 tasks are selected.
 * Uses shared task-actions registry + executors for consistent behavior.
 *
 * Follows master plan §21: count shown in header + per-item labels.
 */

import { memo, useCallback, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import { shortcutLabel } from "@/shared/utils/platform/shortcutLabel"
import { TASK_ACTIONS, useTaskActionExecutors } from "@/features/task-actions"
import { UserStatusPicker } from "@/shared/components/UserStatusPicker"
import { useMenuPosition } from "@/shared/hooks/useMenuPosition"
import { useViewContext } from "@/app/stores/viewContext"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MultiSelectContextMenuProps {
  taskIds: string[]
  position: { x: number; y: number }
  onClose: () => void
}

// ---------------------------------------------------------------------------
// Menu item helpers
// ---------------------------------------------------------------------------

interface MenuItemDef {
  label: string
  shortcut?: string
  destructive?: boolean
  disabled?: boolean
  icon?: React.ReactNode
  onClick: () => void
}

function MenuItem({
  label,
  shortcut,
  destructive,
  disabled,
  icon,
  onClick,
}: MenuItemDef) {
  return (
    <button
      className={cn(
        "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm outline-none transition-colors duration-150",
        "hover:bg-primary/10 focus:bg-primary/10",
        destructive && "text-destructive hover:bg-destructive/10",
        disabled && "pointer-events-none opacity-50",
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && <span className="[&_svg]:size-4">{icon}</span>}
      <span className="flex-1 text-left">{label}</span>
      {shortcut && (
        <span className="ml-4 text-xs tracking-widest opacity-60">
          {shortcut}
        </span>
      )}
    </button>
  )
}

function MenuSeparator() {
  return <div className="my-1 h-px bg-foreground/5" />
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MultiSelectContextMenu = memo(function MultiSelectContextMenu({
  taskIds,
  position,
  onClose,
}: MultiSelectContextMenuProps) {
  const { t } = useTranslation()
  const exec = useTaskActionExecutors(taskIds)
  const count = taskIds.length
  const A = TASK_ACTIONS
  const setStatus = A["set-status"]
  const copyToClip = A["copy-to-clipboard"]
  const [showStatusPicker, setShowStatusPicker] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuStyle = useMenuPosition(position, menuRef)

  // --- Handlers that wrap executors + close menu ---------------------------

  const handleComplete = useCallback(() => {
    onClose()
    exec.complete()
  }, [onClose, exec])

  const handleCancel = useCallback(() => {
    onClose()
    exec.cancel()
  }, [onClose, exec])

  const handleSchedule = useCallback(() => {
    onClose()
    exec.schedule()
  }, [onClose, exec])

  const handleEvening = useCallback(() => {
    onClose()
    exec.evening()
  }, [onClose, exec])

  const handleSomeday = useCallback(() => {
    onClose()
    exec.someday()
  }, [onClose, exec])

  const handleMove = useCallback(() => {
    onClose()
    useViewContext.getState().openMovePicker(taskIds)
  }, [onClose, taskIds])

  const handleDeadline = useCallback(() => {
    onClose()
    exec.deadline()
  }, [onClose, exec])

  const handleDuplicate = useCallback(() => {
    onClose()
    exec.duplicate()
  }, [onClose, exec])

  const handleCopy = useCallback(() => {
    onClose()
    exec.copyToClipboard()
  }, [onClose, exec])

  const handleCut = useCallback(() => {
    onClose()
    exec.cut()
  }, [onClose, exec])

  const handleTags = useCallback(() => {
    onClose()
    useViewContext.getState().openTagPicker(taskIds)
  }, [onClose, taskIds])

  const handleDelete = useCallback(() => {
    onClose()
    void exec.delete()
  }, [onClose, exec])

  const handleStatusPickerClose = useCallback(() => {
    setShowStatusPicker(false)
    onClose()
  }, [onClose])

  return createPortal(
    <>
      {/* Backdrop */}
      <div data-context-menu-backdrop className="fixed inset-0 z-[200] pointer-events-auto" onClick={(e) => { e.stopPropagation(); onClose() }} />

      {/* Menu panel */}
      <div
        ref={menuRef}
        className="fixed z-[200] pointer-events-auto flex min-w-[220px] flex-col overflow-hidden rounded-xl border border-foreground/5 bg-popover p-1.5 text-popover-foreground shadow-[0px_12px_32px_0px_#1111110F,0px_8px_4px_-8px_#1111110D]"
        style={menuStyle}
        role="menu"
      >
        {showStatusPicker ? (
          /* Status picker — replaces main menu content */
          <div className="flex min-h-0 min-w-[180px] flex-col overflow-hidden">
            <UserStatusPicker
              taskId={taskIds}
              onClose={handleStatusPickerClose}
            />
          </div>
        ) : (
          <>
            {/* Header with count */}
            <div className="px-3 py-1 text-xs font-medium text-muted-foreground">
              {t("{{count}} tasks selected", { count })}
            </div>

            <MenuSeparator />

            {/* Status actions */}
            <MenuItem
              label={t(A.complete.labelBulk!)}
              shortcut={shortcutLabel(A.complete.shortcut!)}
              icon={<A.complete.icon />}
              onClick={handleComplete}
            />
            <MenuItem
              label={t(A.cancel.labelBulk!)}
              icon={<A.cancel.icon />}
              onClick={handleCancel}
            />

            <MenuSeparator />

            {/* Schedule */}
            <MenuItem
              label={t(A.schedule.label)}
              shortcut={shortcutLabel(A.schedule.shortcut!)}
              icon={<A.schedule.icon />}
              onClick={handleSchedule}
            />
            <MenuItem
              label={t(A.evening.label)}
              shortcut={shortcutLabel(A.evening.shortcut!)}
              icon={<A.evening.icon />}
              onClick={handleEvening}
            />
            <MenuItem
              label={t(A.someday.label)}
              shortcut={shortcutLabel(A.someday.shortcut!)}
              icon={<A.someday.icon />}
              onClick={handleSomeday}
            />
            <MenuItem
              label={t(A.move.label)}
              shortcut={shortcutLabel(A.move.shortcut!)}
              icon={<A.move.icon />}
              onClick={handleMove}
            />
            <MenuItem
              label={t(A.tags.label)}
              shortcut={shortcutLabel(A.tags.shortcut!)}
              icon={<A.tags.icon />}
              onClick={handleTags}
            />

            <MenuSeparator />

            {/* Deadline + Set Status */}
            <MenuItem
              label={t(A.deadline.label)}
              icon={<A.deadline.icon />}
              onClick={handleDeadline}
            />
            <MenuItem
              label={t(setStatus.label)}
              icon={<setStatus.icon />}
              onClick={() => setShowStatusPicker(true)}
            />

            <MenuSeparator />

            {/* Clipboard + Organize */}
            <MenuItem
              label={t(copyToClip.label)}
              icon={<copyToClip.icon />}
              onClick={handleCopy}
            />
            <MenuItem
              label={t(A.cut.label)}
              shortcut={shortcutLabel(A.cut.shortcut!)}
              icon={<A.cut.icon />}
              onClick={handleCut}
            />
            <MenuItem
              label={t(A.duplicate.labelBulk!)}
              shortcut={shortcutLabel(A.duplicate.shortcut!)}
              icon={<A.duplicate.icon />}
              onClick={handleDuplicate}
            />

            <MenuSeparator />

            {/* Destructive */}
            <MenuItem
              label={t(A.delete.labelBulk!)}
              shortcut="⌫"
              icon={<A.delete.icon />}
              destructive
              onClick={handleDelete}
            />
          </>
        )}
      </div>
    </>,
    document.body,
  )
})
